import React from "react";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import BugsnagPerformance from "@bugsnag/browser-performance";
import { logger } from "@/lib/logger";
import {
  redactSensitive,
  redactTelemetryValue,
} from "@kudoz/shared/redactDiagnostics";

const _win =
  typeof window !== "undefined"
    ? (window as unknown as Record<string, unknown>)
    : undefined;
const apiKey: string =
  (typeof window !== "undefined" &&
    (window as Window & { __BUGSNAG_API_KEY__?: string })
      .__BUGSNAG_API_KEY__) ||
  (import.meta.env.VITE_BUGSNAG_API_KEY as string | undefined) ||
  "";

interface ClientTelemetryEvent {
  request?: Record<string, unknown>;
  errors?: Array<Record<string, unknown>>;
  breadcrumbs?: Array<Record<string, unknown>>;
  context?: unknown;
  _metadata?: Record<string, unknown>;
  addMetadata?: (...args: unknown[]) => unknown;
  setContext?: (context: string) => unknown;
}

const clientEventsWithRedaction = new WeakSet<object>();

/**
 * Browser Bugsnag automatically captures the current URL and request context.
 * Sanitize those fields, plus any structured metadata/breadcrumbs, before the
 * event leaves the browser. Invitation URLs contain bearer credentials.
 */
export function sanitizeClientBugsnagEvent(event: unknown): void {
  if (!event || typeof event !== "object") return;
  const target = event as ClientTelemetryEvent;
  // Explicit Bugsnag.notify callbacks run after the configured onError
  // callback. Wrap event mutators so a later callback cannot add an invite
  // token after the initial scrub.
  if (!clientEventsWithRedaction.has(target)) {
    clientEventsWithRedaction.add(target);
    const addMetadata = target.addMetadata;
    if (typeof addMetadata === "function") {
      target.addMetadata = (...args: unknown[]) => {
        const next = [...args];
        if (next.length >= 2 && typeof next[1] === "string") {
          next[2] = redactTelemetryValue(next[2]);
        } else if (next.length >= 2) {
          next[1] = redactTelemetryValue(next[1]);
        }
        return addMetadata.apply(target, next);
      };
    }
    const setContext = target.setContext;
    if (typeof setContext === "function") {
      target.setContext = (context: string) =>
        setContext.call(target, redactSensitive(context));
    }
  }
  if (target.request && typeof target.request === "object") {
    Object.assign(
      target.request,
      redactTelemetryValue(target.request) as Record<string, unknown>
    );
  }
  if (typeof target.context === "string") {
    target.context = redactSensitive(target.context);
  }
  if (Array.isArray(target.errors)) {
    for (const error of target.errors) {
      if (typeof error.errorMessage === "string") {
        error.errorMessage = redactSensitive(error.errorMessage);
      }
      if (error.stacktrace !== undefined) {
        error.stacktrace = redactTelemetryValue(error.stacktrace);
      }
    }
  }
  if (Array.isArray(target.breadcrumbs)) {
    for (const breadcrumb of target.breadcrumbs) {
      if (typeof breadcrumb.message === "string") {
        breadcrumb.message = redactSensitive(breadcrumb.message);
      }
      if (breadcrumb.metadata !== undefined) {
        breadcrumb.metadata = redactTelemetryValue(breadcrumb.metadata);
      }
    }
  }
  if (target._metadata && typeof target._metadata === "object") {
    const metadata = redactTelemetryValue(target._metadata) as Record<
      string,
      unknown
    >;
    for (const key of Object.keys(target._metadata)) delete target._metadata[key];
    Object.assign(target._metadata, metadata);
  }
}

export function sanitizeClientBugsnagBreadcrumb(breadcrumb: {
  message: string;
  metadata: Record<string, unknown>;
}): void {
  breadcrumb.message = redactSensitive(breadcrumb.message);
  breadcrumb.metadata = redactTelemetryValue(
    breadcrumb.metadata
  ) as Record<string, unknown>;
}

if (apiKey) {
  Bugsnag.start({
    apiKey,
    plugins: [new BugsnagPluginReact()],
    appVersion:
      (typeof window !== "undefined" &&
        (window as Window & { __APP_VERSION__?: string }).__APP_VERSION__) ||
      (import.meta.env.VITE_APP_VERSION as string | undefined) ||
      "dev",
    releaseStage: import.meta.env.MODE,
    enabledReleaseStages: ["production"],
    onBreadcrumb(breadcrumb) {
      sanitizeClientBugsnagBreadcrumb(breadcrumb);
    },
    onError(event) {
      sanitizeClientBugsnagEvent(event);
    },
  });
  BugsnagPerformance.start({ apiKey });
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  FallbackComponent?: React.ComponentType<{ error: Error }>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class FallbackErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    logger.error("[ErrorBoundary] Caught render error:", error, info);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const { FallbackComponent } = this.props;
      if (FallbackComponent) {
        return <FallbackComponent error={this.state.error} />;
      }
      return null;
    }
    return this.props.children;
  }
}

export const BugsnagErrorBoundary: React.ComponentType<ErrorBoundaryProps> =
  apiKey
    ? (
        Bugsnag.getPlugin("react") as {
          createErrorBoundary: (
            react: typeof React
          ) => React.ComponentType<ErrorBoundaryProps>;
        }
      ).createErrorBoundary(React)
    : FallbackErrorBoundary;

export function notifyBugsnag(
  error: Error,
  metadata?: Record<string, unknown>
): void {
  if (!apiKey) return;
  try {
    const safeMetadata = metadata
      ? (redactTelemetryValue(metadata) as Record<string, unknown>)
      : undefined;
    Bugsnag.notify(error, (event) => {
      // The global callback protects automatically captured URL/request data;
      // this callback protects metadata added by this explicit notification.
      if (safeMetadata) event.addMetadata("context", safeMetadata);
      sanitizeClientBugsnagEvent(event);
    });
  } catch {
    // Never let Bugsnag crash the app
  }
}

export { Bugsnag };
